import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Sparkle } from '@phosphor-icons/react';
import axios from 'axios';
import VoiceTextarea from './VoiceTextarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BLOCK_LABELS = {
  hidden_talent: 'Hidden Talent Scan',
  geist: 'Geist',
  koerper: 'Körper',
  wesen: 'Wesen',
  biografie: 'Biografie',
  kompass: 'Kompass',
};

// Typewriter that reveals text character by character
const Typewriter = ({ text, speed = 18, onDone }) => {
  const [shown, setShown] = useState('');
  useEffect(() => {
    setShown('');
    if (!text) return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        if (onDone) onDone();
      }
    }, speed);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return <span>{shown}<span style={{ opacity: shown.length < (text || '').length ? 0.6 : 0, marginLeft: 1 }}>▍</span></span>;
};

const DeepeningQuestion = ({ sessionId, blockId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ question: '', hint: '' });
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [typingDone, setTypingDone] = useState(false);

  const fetchQuestion = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/blocks/deepening/generate`, { session_id: sessionId, block_id: blockId });
      setData({ question: r.data.question || '', hint: r.data.hint || '' });
      setAnswer(r.data.answer || '');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Frage konnte nicht generiert werden.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, blockId]);

  useEffect(() => { fetchQuestion(); }, [fetchQuestion]);

  const submit = async (skip = false) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/blocks/deepening/answer`, {
        session_id: sessionId,
        block_id: blockId,
        answer: skip ? '' : answer.trim(),
        skipped: skip,
      });
      onClose(skip ? 'skipped' : 'answered');
    } catch (e) {
      setError('Antwort konnte nicht gespeichert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const blockLabel = BLOCK_LABELS[blockId] || blockId;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(8px)', zIndex: 100 }}
        data-testid="deepening-modal"
      >
        <motion.div
          initial={{ scale: 0.94, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 280 }}
          className="relative w-full max-w-xl"
          style={{
            background: 'var(--bg-surface)',
            borderRadius: '24px',
            border: '1px solid var(--border-default)',
            boxShadow: '0 30px 80px rgba(15,23,42,0.25)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div className="relative px-6 pt-6 pb-2 flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'rgba(91,184,196,0.15)' }}>
              <Sparkle size={20} weight="duotone" style={{ color: 'var(--accent-cyan)' }} />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.12em' }}>
                EINE LETZTE BEOBACHTUNG · {blockLabel.toUpperCase()}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                DEEPDIVE hat dir zugehört.
              </p>
            </div>
            <button onClick={() => onClose(null)} className="p-2 -m-2"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
              data-testid="deepening-close" aria-label="Schließen">
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 pb-6 pt-3">
            {loading && (
              <div className="py-10 flex flex-col items-center gap-3">
                <motion.div
                  className="hex-clip"
                  style={{ width: 48, height: 48, background: 'var(--accent-cyan)' }}
                  animate={{ scale: [1, 1.12, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                />
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  DEEPDIVE überlegt eine ehrliche Frage für dich…
                </p>
              </div>
            )}

            {!loading && error && (
              <div className="py-8 text-center">
                <p className="text-sm mb-4" style={{ color: '#DC2626' }} data-testid="deepening-error">{error}</p>
                <button onClick={fetchQuestion} className="page-shell-btn">
                  Nochmal versuchen <ArrowRight size={14} />
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                <p className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)', lineHeight: 1.45, minHeight: '3em' }}
                  data-testid="deepening-question">
                  <Typewriter text={data.question} speed={22} onDone={() => setTypingDone(true)} />
                </p>
                {data.hint && typingDone && (
                  <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                    className="text-xs mb-4" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    {data.hint}
                  </motion.p>
                )}

                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: typingDone ? 1 : 0, height: typingDone ? 'auto' : 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ overflow: 'hidden' }}
                >
                  <VoiceTextarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Lass dir Zeit. Schreib oder sprich, was wirklich kommt — ohne Filter."
                    rows={4}
                    autoFocus
                    testId="deepening-answer-textarea"
                    style={{ marginTop: 8 }}
                  />
                  <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
                    <button onClick={() => submit(true)} disabled={submitting}
                      className="text-xs font-semibold px-4 py-2.5"
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--border-default)',
                        borderRadius: '10px',
                        color: 'var(--text-secondary)',
                        cursor: submitting ? 'wait' : 'pointer',
                      }}
                      data-testid="deepening-skip">
                      Später beantworten
                    </button>
                    <button onClick={() => submit(false)} disabled={submitting || answer.trim().length < 5}
                      className="flex items-center gap-2 px-6 py-2.5 text-xs font-semibold"
                      style={{
                        background: answer.trim().length >= 5 ? 'var(--accent-cyan)' : 'var(--border-default)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: submitting ? 'wait' : (answer.trim().length >= 5 ? 'pointer' : 'not-allowed'),
                        boxShadow: answer.trim().length >= 5 ? '0 4px 14px rgba(91,184,196,0.3)' : 'none',
                        opacity: answer.trim().length >= 5 ? 1 : 0.5,
                      }}
                      data-testid="deepening-submit">
                      {submitting ? 'Speichert…' : 'Antwort versiegeln'} <ArrowRight size={14} weight="bold" />
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DeepeningQuestion;
